import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { kickBlue, kickLightBlue, kickNavy } from "~/shared/styles/baseStyles";
import KickClient from "../utils/api";
import { createMedicationReviewPrefetch } from "../utils/medicationReviewPrefetch";
import {
  deduplicateMedicationEntries,
  mergeMedicationEntries,
} from "../utils/medicationDeduplication";
import { getUserID } from "../utils/visitCookies";
import MedicationDetails from "./medicationDetails";
import MedicationSearch from "./medicationSearch";
import {
  medicationHeadingStyles,
  medicationPrimaryButtonStyles,
  medicationSecondaryButtonStyles,
} from "./medicationReviewStyles";

function ReviewDetails({ title, children, divider = true }) {
  return (
    <Box
      component="details"
      sx={{
        borderTop: divider ? "1px solid #e9e6ef" : "none",
        "& > summary": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minHeight: 44,
          py: 1,
          cursor: "pointer",
          color: kickBlue,
          fontSize: "14px",
          listStyle: "none",
          "&::-webkit-details-marker": { display: "none" },
          "&:focus-visible": {
            outline: `2px solid ${kickBlue}`,
            outlineOffset: 2,
            borderRadius: "4px",
          },
        },
        "&[open] > summary svg": { transform: "rotate(180deg)" },
      }}
    >
      <summary>
        {title}
        <ExpandMoreRounded aria-hidden="true" />
      </summary>
      <Box sx={{ pb: 2 }}>{children}</Box>
    </Box>
  );
}
const emptyEntry = () => ({
  name: "",
  dose: "",
  frequency: "",
  form: "",
  route: "",
  productStrength: "",
  ndc: "",
  rxCUI: "",
  taking: true,
});
const isPreviouslyReported = (entry) =>
  Boolean(entry.id || entry.previouslyReported);
const isPast = (entry) =>
  entry.removedAt && !entry.removedAt.startsWith("0001-");
const detailsLabel = (entry) =>
  [
    entry.dose,
    entry.frequency,
    entry.productStrength && `Strength: ${entry.productStrength}`,
    entry.form,
    entry.route,
  ]
    .filter(Boolean)
    .join(" · ");
export const validMedicationReview = (review) =>
  !!review?.confirmed &&
  review.ownerUserID === getUserID() &&
  Array.isArray(review.entries) &&
  review.entries.every((entry) => entry.name?.trim());
// Must match app.MedicationSummary (Go), which overwrites this value on submit.
export const medicationSummary = (entries = []) =>
  entries
    .filter((entry) => entry.taking)
    .map((entry) => {
      const strength = entry.productStrength?.trim();
      return [
        entry.name?.trim(),
        strength && `Strength: ${strength}`,
        entry.dose?.trim(),
        entry.frequency?.trim(),
        entry.form?.trim(),
        entry.route?.trim(),
      ]
        .filter(Boolean)
        .join(", ");
    })
    .join("\n") ||
  "No, not taking any medicines, supplements, herbals, or other prescriptions outside Kick.";
const previewUnavailableMessage =
  "We couldn’t create a list from your previous answer. Review your original answer below and add your medications using search or manual entry.";

export default function MedicationReview({
  answer,
  onChange,
  onConfirm,
  submitting = false,
  medicationPrefetch,
}) {
  const [localPrefetch] = useState(createMedicationReviewPrefetch);
  const previewLoader = medicationPrefetch || localPrefetch;
  const review = answer?.medicationReview;
  const reviewRef = useRef(review);
  reviewRef.current = review;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState("");
  const [historyNotice, setHistoryNotice] = useState("");
  const [feedback, setFeedback] = useState("");
  const [past, setPast] = useState([]);
  const [loadCount, setLoadCount] = useState(0);
  const [editor, setEditor] = useState(null);
  const ownerUserID = getUserID();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setExtracting(false);
    setEditor(null);
    setHistoryNotice("");
    setFeedback("");
    KickClient.newAuthenticatedClient()
      .get("/visits/medications")
      .then(({ data }) => {
        if (!active || getUserID() !== ownerUserID) return;
        const { history, legacyText } = data;
        const saved = history.entries || [];
        setPast(saved.filter(isPast));
        const draft = reviewRef.current;
        setError("");
        if (
          draft?.ownerUserID === ownerUserID &&
          draft.baselineRevision === history.revision &&
          !answer?.reloadRequired
        ) {
          const entries = deduplicateMedicationEntries(draft.entries);
          if (entries.length < draft.entries.length) {
            const next = {
              ...draft,
              entries,
              confirmed: false,
              submissionID: uuidv4(),
            };
            reviewRef.current = next;
            onChangeRef.current(next);
            setFeedback(
              "Removed duplicate medications from your list. Check the details before continuing.",
            );
          }
          setLoading(false);
          return;
        }
        // A stale draft of the patient's own is replaced by the latest saved
        // list without comment; the combined-records notice is for other cases.
        if (draft?.ownerUserID !== ownerUserID && history.needsReview) {
          setHistoryNotice(
            "Your records were combined. Please review every entry, including any duplicates.",
          );
        }
        const next = {
          ownerUserID,
          submissionID: uuidv4(),
          baselineRevision: history.revision,
          confirmed: false,
          sourceText: legacyText || "",
          entries: saved
            .filter((entry) => !isPast(entry))
            .map((entry) => ({ ...entry, restartOfID: "", taking: true })),
        };
        reviewRef.current = next;
        onChangeRef.current(next);
        setLoading(false);
        if (legacyText && draft?.ownerUserID !== ownerUserID) {
          setExtracting(true);
          previewLoader
            .loadPreview(legacyText, history.revision)
            .then(({ data: preview }) => {
              if (!active || getUserID() !== ownerUserID) return;
              const incoming = (preview.entries || []).map((entry) => ({
                ...entry,
                previouslyReported: true,
              }));
              const merged = mergeMedicationEntries(next.entries, incoming);
              const extracted = { ...next, entries: merged.entries };
              reviewRef.current = extracted;
              onChangeRef.current(extracted);
              setFeedback(
                !incoming.length
                  ? previewUnavailableMessage
                  : preview.manualEntry || preview.unresolvedText?.trim()
                    ? "We found some medications in your previous answer. Review the list and add anything missing."
                    : "We found medications in your previous answer. Remove anything you no longer take and check the details.",
              );
            })
            .catch(() => {
              if (active && getUserID() === ownerUserID)
                setFeedback(previewUnavailableMessage);
            })
            .finally(() => {
              if (active && getUserID() === ownerUserID) setExtracting(false);
            });
        }
      })
      .catch(() => {
        if (!active || getUserID() !== ownerUserID) return;
        setError(
          "We couldn't load your saved medication list. Please retry before continuing.",
        );
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadCount, answer?.reloadRequired, ownerUserID, previewLoader]);

  const update = (changes) => {
    const next = {
      ...reviewRef.current,
      ...changes,
      confirmed: false,
      submissionID: uuidv4(),
    };
    reviewRef.current = next;
    onChangeRef.current(next);
  };
  const addEntry = (entry, { openDetails = false } = {}) => {
    const merged = mergeMedicationEntries(reviewRef.current.entries, [entry]);
    update({ entries: merged.entries });
    setFeedback(
      merged.addedCount ? "" : "This medication is already on your list.",
    );
    if (openDetails && merged.addedCount) {
      const index = merged.entries.length - 1;
      setEditor({ index, entry: merged.entries[index] });
    }
  };
  const changeTaking = (index, taking) =>
    update({
      entries: reviewRef.current.entries.map((entry, i) =>
        i === index ? { ...entry, taking } : entry,
      ),
    });
  const removeEntry = (index) => {
    const entry = reviewRef.current.entries[index];
    if (isPreviouslyReported(entry)) changeTaking(index, false);
    else
      update({
        entries: reviewRef.current.entries.filter((_, i) => i !== index),
      });
    setFeedback("");
  };
  const saveDetails = (entry) => {
    if (editor.index === null) addEntry(entry);
    else {
      update({
        entries: deduplicateMedicationEntries(
          reviewRef.current.entries.map((current, index) =>
            index === editor.index ? entry : current,
          ),
        ),
      });
      setFeedback("");
    }
    setEditor(null);
  };
  if (loading)
    return (
      <Box
        role="status"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 3,
          color: kickNavy,
        }}
      >
        <CircularProgress size={20} sx={{ color: kickBlue }} />
        <Typography>Loading your medication list…</Typography>
      </Box>
    );
  if (error)
    return (
      <Alert
        severity="error"
        action={
          <Button
            type="button"
            onClick={() => setLoadCount(loadCount + 1)}
            sx={medicationSecondaryButtonStyles}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  if (!review || review.ownerUserID !== ownerUserID) return null;

  const hasCurrentEntries = review.entries.some((entry) => entry.taking);
  const sourceTooLong = (review.sourceText || "").length > 64000;
  const disabled = extracting || submitting;
  const confirmDisabled =
    disabled ||
    !!editor ||
    sourceTooLong ||
    !review.entries.every((entry) => entry.name?.trim());
  const confirm = () => {
    if (confirmDisabled) return;
    const next = {
      ...reviewRef.current,
      entries: deduplicateMedicationEntries(reviewRef.current.entries),
      confirmed: true,
    };
    reviewRef.current = next;
    onConfirm(next);
  };
  return (
    <Box
      className="ph-no-capture"
      sx={{
        backgroundColor: "#fff",
        color: kickNavy,
        p: { xs: 2.5, sm: 4 },
        mt: { xs: 1, sm: 0 },
        borderRadius: "8px",
        fontFamily: '"BasisGrotesquePro-Regular", Helvetica, Arial, sans-serif',
        "& .MuiButton-root, & .MuiInputBase-root": { fontFamily: "inherit" },
      }}
    >
      {historyNotice && (
        <Alert
          severity="info"
          sx={{ mb: 3, backgroundColor: kickLightBlue, color: kickNavy }}
        >
          {historyNotice}
        </Alert>
      )}
      {sourceTooLong && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Your previous answer is too long. Please contact support for help
          continuing.
        </Alert>
      )}
      <Typography component="h4" sx={medicationHeadingStyles}>
        {hasCurrentEntries ? "Anything else to add?" : "Add your medications"}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          mb: { xs: 2, sm: 3 },
          color: "#626176",
          fontSize: "15px",
          lineHeight: 1.6,
        }}
      >
        Search by name, then add any details you know. Include medications and
        supplements you take outside Kick.
      </Typography>
      <MedicationSearch
        entries={review.entries}
        disabled={disabled || !!editor}
        onSelect={(name) =>
          addEntry({ ...emptyEntry(), name }, { openDetails: true })
        }
        onManual={(name) =>
          setEditor({ index: null, entry: { ...emptyEntry(), name } })
        }
      />
      <Box
        role="status"
        aria-live="polite"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          my: extracting || feedback ? 2 : 0,
          color: kickBlue,
          fontSize: "14px",
        }}
      >
        {extracting ? (
          <>
            <CircularProgress
              size={20}
              aria-hidden="true"
              sx={{ color: "inherit", flexShrink: 0 }}
            />
            <span>Reviewing your previous medication answer…</span>
          </>
        ) : (
          feedback
        )}
      </Box>
      {hasCurrentEntries && (
        <Box sx={{ mt: { xs: 2.5, sm: 3 } }}>
          <Typography
            component="h4"
            sx={{ ...medicationHeadingStyles, mb: { xs: 1.5, sm: 2 } }}
          >
            Your medication list
          </Typography>
          <Box
            component="ul"
            sx={{ display: "grid", gap: 1.5, listStyle: "none", p: 0, m: 0 }}
          >
            {review.entries.map((entry, index) => {
              if (!entry.taking) return null;
              return (
                <Box
                  component="li"
                  key={entry.id || `new-${index}`}
                  sx={{
                    border: "1px solid #e1ddeb",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component={ButtonBase}
                    type="button"
                    disabled={disabled}
                    disableRipple
                    onClick={() =>
                      setEditor({
                        index,
                        entry: { ...emptyEntry(), ...entry },
                      })
                    }
                    aria-label={`Edit details for ${entry.name || "medication"}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "inherit",
                      textAlign: "left",
                      fontFamily: "inherit",
                      color: kickNavy,
                      "&:hover": { backgroundColor: "transparent" },
                      "&.Mui-focusVisible": {
                        outline: `2px solid ${kickBlue}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        component="span"
                        sx={{
                          display: "block",
                          m: 0,
                          fontSize: "16px",
                          fontWeight: 600,
                          color: kickNavy,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {entry.name || "Medication name needed"}
                      </Typography>
                      {detailsLabel(entry) && (
                        <Typography
                          component="span"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: "#626176",
                            fontSize: "14px",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {detailsLabel(entry)}
                        </Typography>
                      )}
                      {entry.previouslyReported && (
                        <Typography
                          component="span"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: "#626176",
                            fontSize: "13px",
                          }}
                        >
                          From your previous answer — please review
                        </Typography>
                      )}
                      {!detailsLabel(entry) && (
                        <Typography
                          component="span"
                          sx={{
                            display: "block",
                            color: kickBlue,
                            mt: 0.5,
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "24px",
                          }}
                        >
                          Add dose and frequency
                        </Typography>
                      )}
                    </Box>
                    <EditOutlined
                      aria-hidden="true"
                      sx={{ color: kickBlue, fontSize: 20, flexShrink: 0 }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {past.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <ReviewDetails
            title={`Past medications (${past.length})`}
            divider={false}
          >
            {past.map((entry) => (
              <Box
                key={entry.id}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 1,
                }}
              >
                <Typography sx={{ fontSize: "15px", overflowWrap: "anywhere" }}>
                  {entry.name} {entry.dose}
                </Typography>
                <Button
                  type="button"
                  variant="outlined"
                  disabled={
                    disabled ||
                    review.entries.some((item) => item.restartOfID === entry.id)
                  }
                  onClick={() =>
                    addEntry({
                      ...emptyEntry(),
                      ...entry,
                      id: "",
                      restartOfID: entry.id,
                      taking: true,
                    })
                  }
                  aria-label={`Taking ${entry.name} again`}
                  sx={medicationSecondaryButtonStyles}
                >
                  Taking again
                </Button>
              </Box>
            ))}
          </ReviewDetails>
        </Box>
      )}
      {review.sourceText && (
        <Box sx={{ mt: { xs: 2.5, sm: 3 } }}>
          <ReviewDetails title="Your previous medications" divider={false}>
            <Typography
              sx={{
                whiteSpace: "pre-wrap",
                color: "#626176",
                fontSize: "14px",
                lineHeight: 1.6,
              }}
            >
              {review.sourceText}
            </Typography>
          </ReviewDetails>
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Button
          type="button"
          fullWidth
          variant="contained"
          disableElevation
          disabled={confirmDisabled}
          onClick={confirm}
          sx={medicationPrimaryButtonStyles}
        >
          {submitting
            ? "Saving…"
            : hasCurrentEntries
              ? "Save and continue"
              : "I don’t take any medications or supplements outside Kick"}
        </Button>
      </Box>
      {editor && (
        <MedicationDetails
          entry={editor.entry}
          isNew={editor.index === null}
          onSave={saveDetails}
          onRemove={
            editor.index === null
              ? undefined
              : () => {
                  removeEntry(editor.index);
                  setEditor(null);
                }
          }
          onClose={() => setEditor(null)}
        />
      )}
    </Box>
  );
}
